type LegKickTouchType = 'NORMAL' | 'BOMB' | 'COMBO';

export class LegKickSoundEngine {
  private readonly timeoutIds = new Set<ReturnType<typeof setTimeout>>();
  private readonly eventCleanups: Array<() => void> = [];
  private unlockIntervalId: ReturnType<typeof setInterval> | null = null;
  private bgAudio: HTMLAudioElement | null = null;
  private readonly preloadedAudio = new Map<string, HTMLAudioElement>();
  private contextAllowsAudio = false;
  private bgMusicShouldPlay = false;
  /** mp3 качаем только после preload() (старт калибровки Leg Kick), не на главном экране. */
  private mediaRequested = false;

  private static readonly BACKGROUND_SRC = '/media/background_music.mp3';
  private static readonly TOUCH_SRC: Record<LegKickTouchType, string> = {
    NORMAL: '/media/target_touch_normal.mp3',
    BOMB: '/media/target_touch_bomb.mp3',
    COMBO: '/media/target_touch_combo.mp3',
  };
  private static readonly BACKGROUND_VOLUME = 0.28;
  private static readonly TOUCH_VOLUME: Record<LegKickTouchType, number> = {
    NORMAL: 1,
    BOMB: 1,
    COMBO: 1,
  };

  constructor() {
    const onGesture = (): void => {
      this.resumeAndWarm();
      // Resume background music only if it's supposed to be playing (not paused/stopped by game logic)
      if (this.bgMusicShouldPlay && this.bgAudio && this.bgAudio.paused) {
        void this.bgAudio.play().catch(() => {
          /* may still be blocked */
        });
      }
    };
    for (const ev of ['click', 'pointerdown', 'touchstart', 'keydown'] as const) {
      document.addEventListener(ev, onGesture, { capture: true, passive: true });
      this.eventCleanups.push(() =>
        document.removeEventListener(ev, onGesture, { capture: true }),
      );
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') this.resumeAndWarm();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.eventCleanups.push(() =>
      document.removeEventListener('visibilitychange', onVisibilityChange),
    );
    window.addEventListener('focus', onGesture);
    this.eventCleanups.push(() => window.removeEventListener('focus', onGesture));

    this.retryAudioUnlock();
  }

  preload(): void {
    this.mediaRequested = true;
    this.preloadAllAudio();
  }

  private preloadAllAudio(): void {
    const allSrcs = [
      LegKickSoundEngine.BACKGROUND_SRC,
      LegKickSoundEngine.TOUCH_SRC.NORMAL,
      LegKickSoundEngine.TOUCH_SRC.BOMB,
      LegKickSoundEngine.TOUCH_SRC.COMBO,
    ];
    for (const src of allSrcs) {
      void this.preloadAudio(src);
    }
  }

  private preloadAudio(src: string): Promise<void> {
    return new Promise((resolve) => {
      try {
        if (this.preloadedAudio.has(src)) {
          resolve();
          return;
        }
        const a = new Audio(src);
        a.preload = 'auto';
        a.load();
        a.addEventListener('canplaythrough', () => {
          this.preloadedAudio.set(src, a);
          resolve();
        }, { once: true });
        a.addEventListener('error', () => resolve(), { once: true });
        setTimeout(() => resolve(), 5000);
      } catch {
        resolve();
      }
    });
  }

  unlockFromButton(btn?: HTMLElement): void {
    this.resumeAndWarm();
    if (btn) btn.classList.add('hidden');
  }

  retryAudioUnlock(): void {
    for (const ms of [0, 40, 120, 400]) {
      this.setManagedTimeout(() => this.resumeAndWarm(), ms);
    }
    this.setManagedTimeout(() => this.trySilentHtml5Audio(), 80);
    if (this.unlockIntervalId !== null) clearInterval(this.unlockIntervalId);
    let ticks = 0;
    this.unlockIntervalId = setInterval(() => {
      this.resumeAndWarm();
      ticks += 1;
      if (ticks > 20) {
        clearInterval(this.unlockIntervalId!);
        this.unlockIntervalId = null;
      }
    }, 350);
  }

  private trySilentHtml5Audio(): void {
    try {
      const a = new Audio(
        'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA',
      );
      a.volume = 0.0001;
      void a.play().then(() => {
        this.contextAllowsAudio = true;
        a.pause();
      }).catch(() => {
        /* blocked */
      });
    } catch {
      /* ignore */
    }
  }

  startBackgroundMusic(): void {
    this.bgMusicShouldPlay = true;
    if (!this.bgAudio) {
      this.bgAudio = new Audio(LegKickSoundEngine.BACKGROUND_SRC);
      this.bgAudio.loop = true;
      this.bgAudio.preload = 'auto';
      this.bgAudio.volume = LegKickSoundEngine.BACKGROUND_VOLUME;
    }
    if (!this.bgAudio.paused) return;

    this.bgAudio.currentTime = 0;
    console.log('[LegKick] startBackgroundMusic: contextAllowsAudio =', this.contextAllowsAudio);

    const attemptPlay = (): void => {
      void this.bgAudio!.play().catch((err) => {
        console.warn('[LegKick] bgAudio play failed:', err);
        // Retry after a short delay
        if (!this.contextAllowsAudio) {
          this.setManagedTimeout(attemptPlay, 100);
        }
      });
    };

    attemptPlay();
  }

  stopBackgroundMusic(): void {
    this.bgMusicShouldPlay = false;
    if (!this.bgAudio) return;
    this.bgAudio.pause();
    this.bgAudio.currentTime = 0;
  }

  playTargetTouch(type: LegKickTouchType): void {
    this.play(LegKickSoundEngine.TOUCH_SRC[type], LegKickSoundEngine.TOUCH_VOLUME[type]);
  }

  private resumeAndWarm(): void {
    this.contextAllowsAudio = true;
    if (!this.mediaRequested) return;
    void this.preloadAudio(LegKickSoundEngine.BACKGROUND_SRC);
    void this.preloadAudio(LegKickSoundEngine.TOUCH_SRC.NORMAL);
    void this.preloadAudio(LegKickSoundEngine.TOUCH_SRC.BOMB);
    void this.preloadAudio(LegKickSoundEngine.TOUCH_SRC.COMBO);
  }

  private play(src: string, volume: number): void {
    try {
      const a = new Audio(src);
      a.volume = volume;
      void a.play().catch(() => {
        /* autoplay blocked or decode error */
      });
    } catch {
      /* ignore */
    }
  }

  dispose(): void {
    this.stopBackgroundMusic();
    this.bgAudio = null;
    if (this.unlockIntervalId !== null) {
      clearInterval(this.unlockIntervalId);
      this.unlockIntervalId = null;
    }
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds.clear();
    this.eventCleanups.forEach((cleanup) => cleanup());
    this.eventCleanups.length = 0;
  }

  private setManagedTimeout(cb: () => void, ms: number): void {
    const id = setTimeout(() => {
      this.timeoutIds.delete(id);
      cb();
    }, ms);
    this.timeoutIds.add(id);
  }
}
